import React, { useState, useEffect } from "react";
import GenerateInviteModal from "./common/GenerateInviteModal";
import ConfirmationModal from "./common/ConfirmationModal";
import DeletePracticeInstructor from "./admin/DeletePracticeInstructor";
import authService from "../services/authService";
import { formatRoleName } from "../utils/stringUtils";

const InvitationItem = ({ invitation, onCopy, onDelete }) => {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const roleLabel = formatRoleName(invitation.roleName);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(invitation.invitationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onCopy && onCopy();
    } catch (error) {
      console.error("Failed to copy invitation link", error);
    }
  };

  const isExpired =
    !invitation.isMultiUse && invitation.expiresAt
      ? new Date(invitation.expiresAt) < new Date()
      : false;

  const daysUntilExpiry =
    !invitation.isMultiUse && invitation.expiresAt
      ? Math.ceil(
          (new Date(invitation.expiresAt) - new Date()) /
            (1000 * 60 * 60 * 24),
        )
      : null;

  return (
    <div className="bg-white border border-pale-dawn rounded-xl p-4 hover:shadow-sm transition-shadow">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        {/* Left: Info */}
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              invitation.isMultiUse
                ? "bg-amber-100 text-amber-600"
                : "bg-blue-50 text-blue-500"
            }`}
          >
            <i
              className={`fas ${
                invitation.isMultiUse ? "fa-link" : "fa-envelope-open"
              }`}
            ></i>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0 mb-1 flex-wrap sm:flex-nowrap">
              <span
                className="font-bold text-grey-dusk truncate min-w-0"
                title={roleLabel}
              >
                {roleLabel}
              </span>

              {invitation.professionType && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium whitespace-nowrap flex-shrink-0">
                  {invitation.professionType}
                </span>
              )}

              {invitation.isMultiUse && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold border border-amber-200 whitespace-nowrap flex-shrink-0">
                  Persistent
                </span>
              )}
            </div>

            <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
              <span title={new Date(invitation.createdAt).toLocaleString()}>
                Created:{" "}
                {new Date(invitation.createdAt).toLocaleDateString("de-CH")}
              </span>

              <span
                className={isExpired ? "text-red-500 font-semibold" : ""}
                title={
                  invitation.expiresAt
                    ? new Date(invitation.expiresAt).toLocaleString()
                    : ""
                }
              >
                {invitation.isMultiUse
                  ? "Valid indefinitely (until regenerated)"
                  : isExpired
                    ? "Expired"
                    : `Expires in ${daysUntilExpiry} days`}
              </span>
            </div>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-3 w-full lg:w-auto lg:flex-shrink-0">
          <div className="flex-1 lg:w-72 relative">
            <button
              type="button"
              onClick={() => setRevealed(!revealed)}
              className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 font-mono cursor-pointer hover:bg-gray-100 transition-colors flex items-center justify-between select-none"
              title="Click to reveal or hide"
            >
              <span className="truncate mr-2 text-left">
                {revealed
                  ? invitation.invitationUrl
                  : "••••••••••••••••••••••••••••••••"}
              </span>
              <i
                className={`fas ${
                  revealed ? "fa-eye-slash" : "fa-eye"
                } text-gray-400 flex-shrink-0`}
              ></i>
            </button>
          </div>

          <button
            onClick={handleCopy}
            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all flex-shrink-0 ${
              copied
                ? "bg-green-100 text-green-600"
                : "bg-white border border-gray-200 text-gray-500 hover:border-sunrise-red hover:text-sunrise-red"
            }`}
            title="Copy link"
          >
            <i className={`fas ${copied ? "fa-check" : "fa-copy"}`}></i>
          </button>
          
          <button
            onClick={() => {
                onDelete(invitation.token);
            }}
            className="w-10 h-10 rounded-lg flex items-center justify-center transition-all flex-shrink-0 bg-white border border-red-200 text-red-500 hover:bg-red-50 hover:border-red-300"
            title="Delete invitation"
          >
            <i className="fas fa-trash-alt"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

const InvitationsPage = () => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invitationToDelete, setInvitationToDelete] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);

  const requestDelete = (token) => {
    setInvitationToDelete(token);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (invitationToDelete) {
      await deleteInvitation(invitationToDelete);
      setShowDeleteModal(false);
      setInvitationToDelete(null);
    }
  };

  const fetchInvitations = async () => {
    setLoading(true);
    try {
      const result = await authService.getActiveInvitations();
      if (result.success) {
        setInvitations(result.invitations || []);
      } else {
        setInvitations([]);
      }
    } catch (error) {
      console.error("Failed to load invitations", error);
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteInvitation = async (token) => {
    setLoading(true);
    try {
        const result = await authService.deleteInvitation(token);
        if (result.success) {
            await fetchInvitations();
        } else {
            console.error(result.message);
        }
    } catch (error) {
        console.error("Failed to delete invitation", error);
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, []);

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="card-brand-elevated p-8 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <i className="fas fa-envelope-open-text text-sunrise-red mr-4"></i>
                  Invitations
                </h1>
                <p className="text-slate-cloud">
                  Generate and manage secure invitation links for Berufsbildner,
                  Practice Instructors and Revisors.
                </p>
              </div>

              <button
                onClick={() => setShowInviteModal(true)}
                className="inline-flex items-center justify-center px-5 py-3 rounded-lg bg-sunrise-red text-white font-semibold hover:bg-sunrise-600 transition-colors"
              >
                <i className="fas fa-plus mr-2"></i>
                Create new Invitation
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Empty / List State */}
          <div className="xl:col-span-2">
            <div className="card-brand overflow-hidden h-full flex flex-col min-h-[360px] md:min-h-[420px] xl:min-h-[500px]">
              {/* Header */}
              {/* Section header in the Management Hub style: light bar with a
                  neutral count chip, not a solid red banner. */}
              <div className="border-b border-pale-dawn/70 bg-pale-dawn/30 px-4 py-3 sm:px-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <h2 className="text-base sm:text-lg font-bold text-grey-dusk flex items-center min-w-0">
                    <i className="fas fa-link text-sunrise-red mr-3 flex-shrink-0"></i>
                    <span className="truncate">Active Invitations</span>
                  </h2>

                  <span className="self-start sm:self-auto bg-ice-white border border-pale-dawn text-grey-dusk px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                    {invitations.length} Active
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-5 md:p-6 flex-1 bg-gray-50/50">
                {loading ? (
                  <div className="flex flex-col items-center justify-center h-full min-h-[220px] text-gray-400 text-center">
                    <i className="fas fa-circle-notch fa-spin text-2xl sm:text-3xl mb-3 text-sunrise-red"></i>
                    <p className="text-sm sm:text-base">
                      Loading invitations...
                    </p>
                  </div>
                ) : invitations.length > 0 ? (
                  <div className="space-y-3 sm:space-y-4">
                    {invitations.map((invitation) => (
                      <InvitationItem
                        key={invitation.token}
                        invitation={invitation}
                        onCopy={() => {}}
                        onDelete={requestDelete}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full min-h-[220px] text-center py-8 sm:py-10">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-ice-white border border-pale-dawn rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-inbox text-gray-300 text-xl sm:text-2xl"></i>
                    </div>

                    <h3 className="text-base sm:text-lg font-bold text-grey-dusk mb-1">
                      No active invitations
                    </h3>

                    <p className="text-gray-500 text-sm mb-5 max-w-sm">
                      Generate your first invitation to get started.
                    </p>

                    <button
                      onClick={() => setShowInviteModal(true)}
                      className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors text-sm"
                    >
                      Create Invitation
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="xl:col-span-1">
            <div className="card-brand overflow-hidden">
              <div className="px-5 py-4 border-b border-pale-dawn bg-ice-white">
                <h3 className="font-bold text-grey-dusk flex items-center">
                  <i className="fas fa-circle-info text-sunrise-red mr-3"></i>
                  How invitations work
                </h3>
              </div>

              <div className="p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-sunrise-red/10 text-sunrise-red flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-shield-alt text-sm"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-grey-dusk">
                      Secure one-time links
                    </div>
                    <div className="text-sm text-gray-600">
                      Each invitation link is generated securely and intended
                      for controlled onboarding.
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-sunrise-red/10 text-sunrise-red flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-clock text-sm"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-grey-dusk">
                      Expiration
                    </div>
                    <div className="text-sm text-gray-600">
                      Generated links expire automatically after 7 days. Or if
                      set to permanent, they expire when a new permanent link is
                      generated.
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-sunrise-red/10 text-sunrise-red flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-user-tie text-sm"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-grey-dusk">
                      Intended roles
                    </div>
                    <div className="text-sm text-gray-600">
                      Use invitations for <strong>Berufsbildner</strong> and{" "}
                      <strong>Practice Instructors</strong> and{" "}
                      <strong>Revisors</strong>.
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-sunrise-red/10 text-sunrise-red flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-user-graduate text-sm"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-grey-dusk">
                      Apprentice onboarding
                    </div>
                    <div className="text-sm text-gray-600">
                      Apprentices should register through the public signup flow
                      instead.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-brand mt-6 p-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-grey-dusk/10 text-grey-dusk flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-lightbulb"></i>
                </div>
                <div>
                  <div className="font-semibold text-grey-dusk mb-1">
                    Recommended usage
                  </div>
                  <p className="text-sm text-gray-600">
                    As the Young Talents Team decide if you want to use a
                    permanent key for everybody or if you want to generate a new
                    key for each new member.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Practice Instructor */}
        <div className="mt-6">
          <DeletePracticeInstructor />
        </div>

        {showInviteModal && (
          <GenerateInviteModal
            onClose={() => setShowInviteModal(false)}
            onSuccess={fetchInvitations}
          />
        )}        
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Invitation"
          message="Are you sure you want to delete this invitation? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          isDanger={true}
          isLoading={loading}
        />      </div>
    </div>
  );
};

export default InvitationsPage;
